import React from 'react'
import RunResultPanel from './RunResultPanel'
import RunHistoryOverflow from './RunHistoryOverflow'

type Props = {
  runs: any[]
  selectedRunId: string | null
  onSelectRun: (runId: string) => void
  selectedRun: any | null
  taskId: string
  taskName?: string
  runsStatus?: string
  afterSummary?: React.ReactNode
}

function RunInProgressView() {
  return (
    <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-blue-100 tw-bg-blue-50 tw-p-3">
      <span className="fa fa-spinner fa-spin tw-mt-0.5 tw-text-lg tw-text-blue-600" aria-hidden />
      <div>
        <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-800">Task is running</p>
        <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-slate-600">
          The AI is working through your task now. Results will appear here when the run finishes — this can take a few minutes.
        </p>
      </div>
    </div>
  )
}

export default function RunHistorySection({
  runs,
  selectedRunId,
  onSelectRun,
  selectedRun,
  taskId,
  taskName,
  runsStatus,
  afterSummary,
}: Props) {
  const hasRuns = Array.isArray(runs) && runs.length > 0
  const inProgress =
    selectedRun?.status === 'running' && !selectedRun?.response_data
  const detailPending =
    Boolean(selectedRunId) &&
    Boolean(selectedRun) &&
    !selectedRun?.response_data &&
    !selectedRun?.error_message &&
    selectedRun?.status !== 'running' &&
    selectedRun?.status !== 'failed'

  return (
    <div>
      {(runsStatus === 'loading' || runsStatus === 'idle') && !hasRuns && (
        <div className="tw-space-y-3">
          <div className="tw-text-sm tw-text-slate-500">Loading runs…</div>
          {afterSummary}
        </div>
      )}
      {runsStatus === 'succeeded' && !hasRuns && (
        <div className="tw-space-y-3">
          <div className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-500">No runs yet</div>
          {afterSummary}
        </div>
      )}
      {hasRuns && (
        <>
          {inProgress ? (
            <div className="tw-space-y-3">
              <div className="tw-flex tw-justify-end">
                <RunHistoryOverflow
                  runs={runs}
                  selectedRunId={selectedRunId}
                  onSelectRun={onSelectRun}
                />
              </div>
              <RunInProgressView />
              {afterSummary}
            </div>
          ) : detailPending ? (
            <div className="tw-space-y-3">
              <div className="tw-flex tw-justify-end">
                <RunHistoryOverflow
                  runs={runs}
                  selectedRunId={selectedRunId}
                  onSelectRun={onSelectRun}
                />
              </div>
              <div className="tw-text-sm tw-text-slate-500">Loading run…</div>
              {afterSummary}
            </div>
          ) : selectedRun ? (
            <RunResultPanel
              key={selectedRun.id}
              responseData={selectedRun.response_data}
              resolvedParams={selectedRun.resolved_parameters}
              run={selectedRun}
              taskId={taskId}
              taskName={taskName}
              errorMessage={selectedRun.error_message}
              runs={runs}
              selectedRunId={selectedRunId}
              onSelectRun={onSelectRun}
              afterSummary={afterSummary}
            />
          ) : (
            <div className="tw-space-y-3">
              <div className="tw-text-sm tw-text-slate-500">Select a run to view its report.</div>
              {afterSummary}
            </div>
          )}
        </>
      )}
    </div>
  )
}
